import { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Prompt, PromptGroup } from '@server/prompts';
import { selectPromptGroups } from '../../../reducers/promptLibraryReducer';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { PromptWithCategory } from './types';

const EMPTY_CATEGORIES: PromptGroup[] = [];

export const usePromptGroupState = () => {
  const categoriesFromStore = useSelector(selectPromptGroups) ?? EMPTY_CATEGORIES;
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const { promptsLibraryV2 } = useFeatureToggle();

  const categories = useMemo(() => {
    if (promptsLibraryV2) {
      return [...categoriesFromStore].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    }
    return categoriesFromStore;
  }, [categoriesFromStore, promptsLibraryV2]);

  const allPrompts: PromptWithCategory[] = useMemo(() => {
    return categories.flatMap((category) =>
      category.prompts
        .filter((prompt) => promptsLibraryV2 ? prompt.id !== '' : true)
        .map((prompt) => ({
          ...prompt,
          categoryName: category.name,
          categoryId: category.id,
        })),
    );
  }, [categories, promptsLibraryV2]);

  const filteredPrompts: PromptWithCategory[] = useMemo(() => {
    if (!selectedCategoryId) {
      return allPrompts;
    }
    return allPrompts.filter((prompt) => prompt.categoryId === selectedCategoryId);
  }, [allPrompts, selectedCategoryId]);

  const totalPromptsCount = allPrompts.length;

  const handleCategorySelect = useCallback((categoryId: string | null) => {
    setSelectedCategoryId(categoryId);
  }, []);

  const handlePromptSelect = useCallback((prompt: Prompt | null) => {
    setSelectedPrompt(prompt);
  }, []);

  const clearSelectedCategoryIfDeleted = useCallback(
    (deletedCategoryId: string) => {
      if (selectedCategoryId === deletedCategoryId) {
        setSelectedCategoryId(null);
      }
    },
    [selectedCategoryId],
  );

  return {
    categories,
    allPrompts,
    filteredPrompts,
    totalPromptsCount,

    selectedCategoryId,
    selectedPrompt,
    handleCategorySelect,
    handlePromptSelect,
    clearSelectedCategoryIfDeleted,
  };
};
